<!-- Form input for a numeric value with optional min, max, and step constraints. -->

<script lang="ts" setup>
import type { FormFieldController } from '@nzyme/vue-forms/components/defineFormField.js';
import { useNumberInput } from '@nzyme/vue-forms/components/useNumberInput.jsx';
import { vFocus } from '@nzyme/vue-utils/directives/vFocus.js';
import type { Slots } from '@nzyme/vue-utils/slots.js';

import FormFieldWrapper from './FormFieldWrapper.vue';

defineProps({
  ...useNumberInput.props,
  focus: Boolean,
  min: Number,
  max: Number,
  step: Number,
});

defineEmits(useNumberInput.emits);
defineSlots<
  Slots<{
    addon: { field: FormFieldController<number>; inputId: string };
    info: { field: FormFieldController<number> };
  }>
>();

const { NumberInput, field } = useNumberInput();
</script>

<template>
  <FormFieldWrapper
    :field="field"
    :label="label"
    :required="required"
    :disabled="disabled"
    :readonly="readonly"
  >
    <template #default="{ inputId }">
      <NumberInput
        :id="inputId"
        v-focus="focus"
        :min="min"
        :max="max"
        :step="step"
        :class="css.input"
      />
    </template>

    <template #addon="{ inputId }">
      <slot
        name="addon"
        :field="field"
        :input-id="inputId"
      />
    </template>

    <template #info>
      <slot
        name="info"
        :field="field"
      />
    </template>
  </FormFieldWrapper>
</template>

<style lang="scss" module="css">
.input {
  /* Hide number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
